/**
 * On-demand reveal of a school's full bank account number on the Org Portal school Details page.
 *
 * The page renders only "Account ending in 1234". Submitting the reveal form posts to the server,
 * which returns the full number and records the reveal in the audit log. Hiding puts the masked
 * text back and drops the number from the page, so showing it again is a new, audited request.
 * Leaving the page hides it too, and throws away a reveal still loading, so the back/forward cache
 * never restores it revealed.
 *
 * Markup (see Areas/OrgPortal/Views/Schools/Details.cshtml):
 *   [data-account-reveal]              container; carries the show/hide labels, tooltips and error text
 *     [data-account-reveal-value]      the masked text, swapped for the full number
 *     form[data-account-reveal-form]   posts to RevealAccountNumber with the antiforgery token
 *       [data-account-reveal-toggle]   the eye button
 *     [data-account-reveal-copy]       copy button, shown only while the number is revealed
 *     [data-account-reveal-error]      error message
 */

const CONTAINER_SELECTOR = '[data-account-reveal]';

interface RevealResponse {
    accountNumber?: string;
}

interface ToggleText {
    /** The button's accessible name, which names the action. */
    label: string;
    /** The hover tooltip. */
    tooltip: string;
}

function setToggleState(toggle: HTMLButtonElement, revealed: boolean, text: ToggleText): void {
    toggle.setAttribute('aria-label', text.label);
    toggle.title = text.tooltip;

    const icon = toggle.querySelector<HTMLElement>('i');
    icon?.classList.toggle('ti-eye', !revealed);
    icon?.classList.toggle('ti-eye-off', revealed);
}

async function fetchAccountNumber(form: HTMLFormElement): Promise<string | null> {
    const response = await fetch(form.action, {
        method: 'POST',
        // FormData carries the form's __RequestVerificationToken.
        body: new FormData(form),
        headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        },
    });

    if (!response.ok) {
        return null;
    }

    // An expired session redirects to the sign-in page, which arrives as HTML and fails to parse.
    const body = await response.json().catch(() => null) as RevealResponse | null;
    return body?.accountNumber || null;
}

function bindContainer(container: HTMLElement): void {
    if (container.dataset.accountRevealBound === 'true') {
        return;
    }

    const value = container.querySelector<HTMLElement>('[data-account-reveal-value]');
    const form = container.querySelector<HTMLFormElement>('[data-account-reveal-form]');
    const toggle = container.querySelector<HTMLButtonElement>('[data-account-reveal-toggle]');
    if (!value || !form || !toggle) {
        return;
    }

    container.dataset.accountRevealBound = 'true';

    const copy = container.querySelector<HTMLElement>('[data-account-reveal-copy]');
    const error = container.querySelector<HTMLElement>('[data-account-reveal-error]');
    const maskedText = value.textContent ?? '';
    const showText: ToggleText = {
        label: container.dataset.accountRevealShowLabel ?? 'Reveal account number',
        tooltip: container.dataset.accountRevealShowTooltip ?? 'Click to reveal the account number',
    };
    const hideText: ToggleText = {
        label: container.dataset.accountRevealHideLabel ?? 'Hide account number',
        tooltip: container.dataset.accountRevealHideTooltip ?? 'Click to hide the account number',
    };
    const errorLabel = container.dataset.accountRevealErrorLabel ?? 'The account number couldn\'t be loaded.';
    let revealed = false;

    const setError = (message: string | null): void => {
        if (!error) {
            return;
        }

        error.textContent = message ?? '';
        error.classList.toggle('d-none', !message);
    };

    const setRevealed = (accountNumber: string | null): void => {
        revealed = accountNumber !== null;
        value.textContent = accountNumber ?? maskedText;
        setToggleState(toggle, revealed, revealed ? hideText : showText);
        copy?.classList.toggle('d-none', !revealed);
    };

    // Bumped each time the page is left, so a reveal still loading then is thrown away when it lands.
    let pageVisit = 0;

    // The back/forward cache restores a page with its DOM as it was, so mask the number on the way
    // out, and again on the way back in in case anything changed the page while it was hidden.
    const resetForPageChange = (): void => {
        pageVisit++;
        setError(null);
        setRevealed(null);
    };
    window.addEventListener('pagehide', resetForPageChange);
    window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
            resetForPageChange();
        }
    });

    form.addEventListener('submit', async (event) => {
        event.preventDefault();
        setError(null);

        if (revealed) {
            setRevealed(null);
            return;
        }

        const visit = pageVisit;
        toggle.disabled = true;
        try {
            const accountNumber = await fetchAccountNumber(form);
            if (visit !== pageVisit) {
                return;
            }

            if (accountNumber) {
                setRevealed(accountNumber);
            } else {
                setError(errorLabel);
            }
        } catch {
            if (visit === pageVisit) {
                setError(errorLabel);
            }
        } finally {
            toggle.disabled = false;
        }
    });
}

/**
 * Binds every account number reveal on the page. A no-op when there are none.
 */
export function initAccountNumberReveal(): void {
    document.querySelectorAll<HTMLElement>(CONTAINER_SELECTOR).forEach(bindContainer);
}
